import type {
  ImageCaptchaResponse,
  UserInfo,
} from '@/types/auth'
/**
 * 用户信息状态模块 - v2版本
 * @description 管理用户信息、认证状态和权限，支持多种登录方式
 * @version v2 - 专为v2架构设计，与旧版独立存储
 *
 * 📐 架构说明：
 * 本 Store 采用「组合模式」，将 874 行的巨型文件拆分为多个独立子模块：
 * - authComposable.ts     — 登录/登出/注册/Token 刷新
 * - captchaComposable.ts  — 图形验证码/手机验证码
 * - userProfileComposable.ts — 用户信息/头像管理/身份验证
 * - devComposable.ts      — 开发环境调试工具
 * 主文件仅负责 State 定义、计算属性和子模块组装，保持 API 表面不变。
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { STORAGE_KEYS } from '@/constants'

import { PermissionCore } from '@/services/PermissionCore'
import {
  LoginType,
  UserRole,
} from '@/types/auth'

import { safeLocalStorage } from '@/utils/storage'
// 导入子模块 composables
import { useAuthActions } from './authComposable'
import { useCaptchaActions } from './captchaComposable'
import { useDevActions } from './devComposable'
import { getFallbackPermissions } from './permissionHelpers'
import { useUserProfileActions } from './userProfileComposable'

export const useUserStorev2 = defineStore(
  'user-v2',
  () => {
    // ==================== State ====================

    const userInfo = ref<UserInfo | null>(null)
    const token = ref<string | null>(null)
    const refreshToken = ref<string | null>(null)
    const permissions = ref<string[]>([])
    const loginTime = ref<number | null>(null)
    /** Token 有效期（秒），从登录响应动态获取 */
    const tokenExpiresIn = ref<number | null>(null)

    // 验证码相关状态
    const imageCaptcha = ref<ImageCaptchaResponse | null>(null)
    const phoneCodeSending = ref(false)
    const phoneCodeCooldown = ref(0)

    // 登录状态管理
    const loginLoading = ref(false)
    const loginType = ref<LoginType>(LoginType.PASSWORD)

    // 权限服务实例
    const permissionCore = new PermissionCore()

    // ==================== Computed ====================

    const isLoggedIn = computed(() => {
      const loggedIn = !!token.value && !!userInfo.value
      return loggedIn
    })
    const isGuest = computed(() => !isLoggedIn.value)
    const isAdmin = computed(() => userInfo.value?.role === 'admin')
    const isDesigner = computed(() => userInfo.value?.role === 'designer')
    const isManufacturer = computed(
      () => userInfo.value?.role === 'manufacturer',
    )
    const isVerified = computed(() => userInfo.value?.isVerified ?? false)
    const userName = computed(() => {
      const info = userInfo.value
      if (!info)
        return '未知用户'
      if (info.nickname)
        return info.nickname
      if (info.username)
        return info.username
      if (info.phone)
        return info.phone.replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2')
      return '未知用户'
    })
    const userAvatar = computed(() => userInfo.value?.avatar || '')

    // 权限检查
    const hasPermission = computed(() => (permission: string) => {
      if (isAdmin.value)
        return true
      return permissions.value.includes(permission)
    })

    const hasAnyPermission = computed(() => (permissionList: string[]) => {
      if (isAdmin.value)
        return true
      return permissionList.some(permission =>
        permissions.value.includes(permission),
      )
    })

    const hasAllPermissions = computed(() => (permissionList: string[]) => {
      if (isAdmin.value)
        return true
      return permissionList.every(permission =>
        permissions.value.includes(permission),
      )
    })

    // ==================== Core Setters ====================

    function setUserInfo(info: UserInfo | null) {
      if (info) {
        // 确保角色字段正确映射到UserRole枚举
        if (info.role && typeof info.role === 'string') {
          const roleValue = info.role
          if (Object.values(UserRole).includes(roleValue as UserRole)) {
            info.role = roleValue as UserRole
          }
          else {
            info.role = UserRole.USER
          }
        }
      }
      userInfo.value = info
    }

    function setToken(newToken: string | null) {
      token.value = newToken
      if (newToken) {
        loginTime.value = Date.now()
      }
      else {
        loginTime.value = null
      }
    }

    function setRefreshToken(newRefreshToken: string | null) {
      refreshToken.value = newRefreshToken
    }

    function setPermissions(newPermissions: string[]) {
      permissions.value = newPermissions
    }

    // 🔧 权限生成函数
    /**
     * 根据用户角色生成完整权限列表
     * @param role 用户角色
     * @param customPermissions 自定义权限（可选）
     * @returns 完整权限数组
     */
    function generatePermissionsForRole(role: UserRole, customPermissions?: string[]): string[] {
      try {
        // 使用PermissionCore服务获取完整权限
        const corePermissions = permissionCore.getUserPermissions(role, customPermissions)

        return corePermissions
      }
      catch (error: unknown) {
        // 降级到基础权限（PermissionCore 异常时）
        console.warn('[UserStore] 权限生成失败，使用基础权限:', error)
        return getFallbackPermissions(role)
      }
    }

    /**
     * 根据用户信息智能设置权限
     * @param info 用户信息
     * @param customPermissions 自定义权限（可选）
     */
    function setPermissionsFromUserInfo(info: UserInfo | null, customPermissions?: string[]) {
      if (!info || !info.role) {
        setPermissions([])
        return
      }

      const rolePermissions = generatePermissionsForRole(info.role, customPermissions)
      setPermissions(rolePermissions)
    }

    function addPermission(permission: string) {
      if (!permissions.value.includes(permission)) {
        permissions.value.push(permission)
      }
    }

    function removePermission(permission: string) {
      const index = permissions.value.indexOf(permission)
      if (index > -1) {
        permissions.value.splice(index, 1)
      }
    }

    // 设置图形验证码
    function setImageCaptcha(captcha: ImageCaptchaResponse | null) {
      imageCaptcha.value = captcha
    }

    // 设置登录类型
    function setLoginType(type: LoginType) {
      loginType.value = type
    }

    // ==================== Composable Actions ====================

    // 认证相关 actions
    const authActions = useAuthActions({
      token,
      refreshToken,
      userInfo,
      loginLoading,
      loginType,
      loginTime,
      tokenExpiresIn,
      imageCaptcha,
      phoneCodeSending,
      phoneCodeCooldown,
      setToken,
      setRefreshToken,
      setUserInfo,
      setPermissions,
      setPermissionsFromUserInfo,
      setLoginType,
      setImageCaptcha,
    })

    // 验证码相关 actions
    const captchaActions = useCaptchaActions({
      imageCaptcha,
      phoneCodeSending,
      phoneCodeCooldown,
      setImageCaptcha,
    })

    // 用户信息管理 actions
    const profileActions = useUserProfileActions({
      userInfo,
      setUserInfo,
      setPermissionsFromUserInfo,
    })

    // 开发调试 actions
    const devActions = useDevActions({
      userInfo,
      token,
      refreshToken,
      permissions,
      loginTime,
      isLoggedIn,
      setToken,
      setUserInfo,
      setPermissionsFromUserInfo,
      logout: authActions.logout,
    })

    // ==================== Return ====================

    return {
      // 状态
      userInfo,
      token,
      refreshToken,
      permissions,
      loginTime,
      imageCaptcha,
      phoneCodeSending,
      phoneCodeCooldown,
      loginLoading,
      loginType,
      tokenExpiresIn,

      // 计算属性
      isLoggedIn,
      isGuest,
      isAdmin,
      isDesigner,
      isManufacturer,
      isVerified,
      userName,
      userAvatar,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,

      // 方法
      setUserInfo,
      setToken,
      setRefreshToken,
      setPermissions,
      setPermissionsFromUserInfo, // 🔧 新增智能权限设置方法
      generatePermissionsForRole, // 🔧 新增权限生成方法
      addPermission,
      removePermission,
      setImageCaptcha,
      setLoginType,
      ...captchaActions,

      // 认证相关方法
      ...authActions,

      // 其他方法
      ...profileActions,

      // 开发助手（仅开发环境）
      ...devActions,
    }
  },
  {
    persist: {
      key: STORAGE_KEYS.USER_STORE_V2,
      storage: safeLocalStorage,
      paths: ['userInfo', 'token', 'refreshToken', 'permissions', 'loginTime', 'tokenExpiresIn'],
      afterRestore: (ctx) => {
        // 在从localStorage恢复数据后，验证并修复用户角色和权限
        if (ctx.store.userInfo && ctx.store.userInfo.role) {
          const currentRole = ctx.store.userInfo.role

          // 如果角色不在有效枚举中，修复为USER
          if (!Object.values(UserRole).includes(currentRole as UserRole)) {
            ctx.store.userInfo.role = UserRole.USER
          }

          // 确保角色是正确的枚举类型
          ctx.store.userInfo.role = ctx.store.userInfo.role as UserRole

          // 🔧 重要修复：恢复时重新生成权限
          try {
            const correctPermissions = ctx.store.generatePermissionsForRole(ctx.store.userInfo.role)
            if (correctPermissions.length !== ctx.store.permissions.length) {
              ctx.store.setPermissions(correctPermissions)
            }
          }
          catch (error: unknown) {
            console.warn('[UserStore] 从存储恢复时权限重新生成失败:', error)
            // 保留原有权限，不做破坏性修改
          }
        }
      },
    },
  },
)

export type UserState = ReturnType<typeof useUserStorev2>
